// @ts-nocheck
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Bell, User, ChevronRight, Home, Users, CheckSquare, BarChart3, Settings, UserPlus, Contact, DollarSign, Headphones, Calendar, FileText, Briefcase, Shield, Ticket, LogOut, Package, BookOpen, Receipt, Building2, CreditCard } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import AppLauncher from './AppLauncher';
import ThemeToggle from './ThemeToggle';
import { GlobalSearch } from './GlobalSearch';
import { apps } from '../../data/mockData';
import { persistedTaskRecords } from '../../services/mockData/tasks';
import { persistedCaseRecords } from '../../services/mockData/cases';
import { persistedSupportTickets } from '../../services/mockData/supportTickets';
import { useGobodoRecords } from '../../services/queries/gobodoRecordQueries';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { useRoles } from '../../services/queries/roleQueries';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useSwitchUser } from '../../contexts/SwitchUserContext';
import { notificationService } from '../../services/notificationService';

// Notification type for tasks, cases, and support tickets
interface Notification {
  id: string;
  type: 'task' | 'case' | 'support_ticket';
  title: string;
  message: string;
  priority: string;
  isRead: boolean;
  timestamp?: string;
}

// Key for storing read notification IDs in localStorage
const READ_NOTIFICATIONS_KEY = 'crm_read_notifications';

// Helper to get read notification IDs from localStorage
const getReadNotificationIds = (): Set<string> => {
  try {
    const stored = localStorage.getItem(READ_NOTIFICATIONS_KEY);
    if (stored) {
      return new Set(JSON.parse(stored));
    }
  } catch (e) {
    console.error('Error reading notification state:', e);
  }
  return new Set();
};

// Helper to mark a notification as read
const markNotificationAsRead = (notificationKey: string): void => {
  try {
    const readIds = getReadNotificationIds();
    readIds.add(notificationKey);
    localStorage.setItem(READ_NOTIFICATIONS_KEY, JSON.stringify([...readIds]));
  } catch (e) {
    console.error('Error saving notification state:', e);
  }
};

// Helper to mark all notifications as read
const markAllNotificationsAsRead = (notificationKeys: string[]): void => {
  try {
    const readIds = getReadNotificationIds();
    notificationKeys.forEach(key => readIds.add(key));
    localStorage.setItem(READ_NOTIFICATIONS_KEY, JSON.stringify([...readIds]));
  } catch (e) {
    console.error('Error saving notification state:', e);
  }
};

interface GlobalNavigationProps {
  currentView?: string;
  onNavigate?: (view: string) => void;
  userRole?: string;
  onRoleChange?: (role: string) => void;
  showMainNavigation?: boolean;
  onAppSelect?: (app: any) => void;
  onNavigateToTask?: (taskId: string) => void;
  onNavigateToCase?: (caseId: string) => void;
}

const GlobalNavigation: React.FC<GlobalNavigationProps> = ({
  currentView = 'home',
  onNavigate = () => {},
  userRole = 'employee',
  onRoleChange = () => {},
  showMainNavigation = true,
  onAppSelect = () => {},
  onNavigateToTask,
  onNavigateToCase
}) => {
  const { logout, user } = useAuth();
  const [showAppLauncher, setShowAppLauncher] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Refs for click-outside detection
  const appLauncherRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Ref for dropdown hover timeout
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Feature flags for MVP mode
  const { isNavItemEnabled, isNavGroupEnabled, mvpMode } = useFeatureFlags();

  // PE-835: Check if viewing as another user (for banner offset)
  const { isSwitched } = useSwitchUser();

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (appLauncherRef.current && !appLauncherRef.current.contains(event.target as Node)) {
        setShowAppLauncher(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Clean up dropdown timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, []);

  // State to trigger re-render when notifications are marked as read
  const [readUpdateTrigger, setReadUpdateTrigger] = useState(0);

  const shouldGobodo = shouldUseGobodo();
  // Only fetch task/case records when the user opens the notifications dropdown
  const { data: gobodoTasks } = useGobodoRecords('Task__c', { enabled: showNotifications });
  const { data: gobodoCases } = useGobodoRecords('Case__c', { enabled: showNotifications });

  // Generate notifications from tasks and cases
  const notifications = useMemo<Notification[]>(() => {
    const today = new Date().toISOString().split('T')[0];
    const allNotifications: Notification[] = [];
    const readIds = getReadNotificationIds();

    const tasks = (shouldGobodo && gobodoTasks) ? gobodoTasks : persistedTaskRecords.get();

    tasks.forEach((task: any) => {
      const status = task.status ?? task.Status__c;
      const priority = task.priority ?? task.Priority__c;
      const dueDate = (task.dueDate ?? task.DueDate__c) as string;
      const isNotCompleted = status !== 'Completed';
      const isHighPriority = priority === 'High' || priority === 'Urgent';
      const isDueToday = dueDate === today;
      const isOverdue = dueDate && new Date(dueDate) < new Date(today);

      if (isNotCompleted && (isHighPriority || isDueToday || isOverdue)) {
        let message = '';
        if (isOverdue) {
          message = `Overdue: ${dueDate}`;
        } else if (isDueToday) {
          message = 'Due today';
        } else {
          message = `Due: ${dueDate || 'No date'}`;
        }

        const notificationKey = `task-${task.id}`;
        allNotifications.push({
          id: task.id as string,
          type: 'task',
          title: (task.title ?? task.subject ?? task.Subject__c ?? 'Untitled Task') as string,
          message,
          priority: (priority as string) || 'Medium',
          isRead: readIds.has(notificationKey),
          timestamp: (task.assignedDate ?? task.createdDate ?? task.CreatedDate__c) || new Date().toISOString()
        });
      }
    });

    const cases = (shouldGobodo && gobodoCases) ? gobodoCases : persistedCaseRecords.get();

    cases.forEach((caseItem: any) => {
      const status = caseItem.status ?? caseItem.Status__c;
      const priority = caseItem.priority ?? caseItem.Priority__c;
      const isNotClosed = status !== 'Closed';
      const isHighPriority = priority === 'High' || priority === 'Critical';
      const isEscalated = caseItem.escalated === true;

      if (isNotClosed && (isHighPriority || isEscalated)) {
        let message = '';
        if (isEscalated) {
          message = `Escalated • ${priority} priority`;
        } else {
          message = `${priority} priority`;
        }

        const notificationKey = `case-${caseItem.id}`;
        allNotifications.push({
          id: caseItem.id as string,
          type: 'case',
          title: (caseItem.subject ?? caseItem.caseNumber ?? caseItem.Subject__c ?? 'Untitled Case') as string,
          message,
          priority: (priority as string) || 'Medium',
          isRead: readIds.has(notificationKey),
          timestamp: (caseItem.createdDate ?? caseItem.CreatedDate__c) || new Date().toISOString()
        });
      }
    });

    // PE-764: Support ticket notifications (only for system_admin)
    if (userRole === 'system_admin') {
      const supportTickets = persistedSupportTickets.get();

      // Show all Open or In Progress support tickets to admins
      supportTickets.forEach(ticket => {
        const isActionable = ticket.status === 'Open' || ticket.status === 'In Progress' || ticket.status === 'Pending';
        const isHighPriority = ticket.priority === 'High' || ticket.priority === 'Critical';

        if (isActionable) {
          let message = `${ticket.category} • ${ticket.status}`;
          if (ticket.submittedBy) {
            message = `From: ${ticket.submittedBy} • ${ticket.status}`;
          }

          const notificationKey = `support_ticket-${ticket.id}`;
          allNotifications.push({
            id: ticket.id,
            type: 'support_ticket',
            title: ticket.title,
            message,
            priority: ticket.priority,
            isRead: readIds.has(notificationKey),
            timestamp: ticket.createdDate || ticket.submittedDate || new Date().toISOString()
          });
        }
      });
    }

    // Workflow/automation notifications from notificationService
    try {
      const workflowNotifs = notificationService.getAll();
      workflowNotifs.slice(0, 50).forEach(wn => {
        const notificationKey = `workflow-${wn.id}`;
        allNotifications.push({
          id: wn.id,
          type: 'task', // Display alongside tasks in the existing UI
          title: wn.workflowRuleName || 'Workflow Notification',
          message: wn.message,
          priority: wn.priority === 'High' ? 'High' : wn.priority === 'Low' ? 'Low' : 'Medium',
          isRead: wn.read || readIds.has(notificationKey),
          timestamp: wn.createdAt,
        });
      });
    } catch {
      // notificationService may not be initialized yet
    }

    // Dedupe by key so same task/case doesn't appear twice (keeps badge count correct)
    const seenKeys = new Set<string>();
    const deduped = allNotifications.filter(n => {
      const key = `${n.type}-${n.id}`;
      if (seenKeys.has(key)) return false;
      seenKeys.add(key);
      return true;
    });

    const priorityOrder: Record<string, number> = {
      'Critical': 0,
      'Urgent': 1,
      'High': 2,
      'Medium': 3,
      'Normal': 4,
      'Low': 5
    };

    return deduped.sort((a, b) => {
      // Unread notifications first
      if (a.isRead !== b.isRead) {
        return a.isRead ? 1 : -1;
      }
      // Then by priority
      return (priorityOrder[a.priority] ?? 5) - (priorityOrder[b.priority] ?? 5);
    });
  }, [readUpdateTrigger, userRole, shouldGobodo, gobodoTasks, gobodoCases]);

  // Count only unread notifications for the badge
  const unreadCount = notifications.filter(n => !n.isRead).length;

  // Format notification timestamp
  const formatNotificationTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // Handle notification click - navigate to detail and mark as read
  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    const notificationKey = `${notification.type}-${notification.id}`;
    markNotificationAsRead(notificationKey);
    setReadUpdateTrigger(prev => prev + 1);

    // Navigate to the appropriate detail page
    if (notification.type === 'task') {
      if (onNavigateToTask) {
        onNavigateToTask(notification.id);
      } else {
        // Fallback: navigate to tasks list
        onNavigate('tasks');
      }
    } else if (notification.type === 'case') {
      if (onNavigateToCase) {
        onNavigateToCase(notification.id);
      } else {
        // Fallback: navigate to cases list
        onNavigate('cases');
      }
    } else if (notification.type === 'support_ticket') {
      // PE-764: Navigate to support tickets page
      onNavigate('support-tickets');
    }

    setShowNotifications(false);
  };

  // Handle mark all as read
  const handleMarkAllAsRead = () => {
    const allKeys = notifications.map(n => `${n.type}-${n.id}`);
    markAllNotificationsAsRead(allKeys);
    setReadUpdateTrigger(prev => prev + 1);
  };

  // Dropdown hover handlers with 300ms delay
  const handleDropdownEnter = (dropdownName: string) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setOpenDropdown(dropdownName);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 300); // 300ms delay before closing
  };

  return (
    <header className={`bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700 fixed left-0 right-0 z-50 ${isSwitched ? 'top-11' : 'top-0'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-4">
            <div
              className="relative"
              ref={appLauncherRef}
            >
              <button
                onClick={() => setShowAppLauncher(!showAppLauncher)}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
              >
                <img
                  src="/regal-logo.svg"
                  alt="Regal"
                  className="w-12 h-12"
                />
                <ChevronRight className="h-4 w-4" />
              </button>
              {showAppLauncher && (
                <AppLauncher
                  apps={apps}
                  currentApp={currentView}
                  onAppSelect={onAppSelect}
                  onClose={() => setShowAppLauncher(false)}
                />
              )}
            </div>

            {showMainNavigation && (
              <nav className="hidden md:flex items-center space-x-4 ml-8">
                {/* Home - Standalone */}
                <button
                  onClick={() => onNavigate('home')}
                  className={`flex items-center space-x-1 px-3 py-2 text-sm font-medium rounded-md ${
                    currentView === 'home' ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Home className="h-4 w-4" />
                  <span>Home</span>
                </button>

                {/* SALES Dropdown - Only show if sales group is enabled or clients are enabled */}
                {(isNavGroupEnabled('sales') || isNavItemEnabled('clients')) && (
                <div
                  className="relative"
                  onMouseEnter={() => handleDropdownEnter('sales')}
                  onMouseLeave={handleDropdownLeave}
                >
                  <button className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md">
                    <span>Sales</span>
                    <ChevronRight className={`h-3 w-3 transform transition-transform ${openDropdown === 'sales' ? 'rotate-90' : ''}`} />
                  </button>

                  {openDropdown === 'sales' && (
                    <div className="absolute left-0 top-full mt-1 z-50">
                      <div
                        className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl py-2 min-w-[160px]"
                        onMouseEnter={() => handleDropdownEnter('sales')}
                        onMouseLeave={handleDropdownLeave}
                      >
                      {isNavItemEnabled('leads') && (
                      <button
                        onClick={() => onNavigate('leads')}
                        className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${
                          currentView === 'leads'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <UserPlus className="h-4 w-4" />
                        <span>Leads</span>
                      </button>
                      )}
                      {isNavItemEnabled('contacts') && (
                      <button
                        onClick={() => onNavigate('contacts')}
                        className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${
                          currentView === 'contacts'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <Contact className="h-4 w-4" />
                        <span>Contacts</span>
                      </button>
                      )}
                      {isNavItemEnabled('clients') && (
                      <button
                        onClick={() => onNavigate('clients')}
                        className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${
                          currentView === 'clients'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <Users className="h-4 w-4" />
                        <span>Clients</span>
                      </button>
                      )}
                      <button
                        onClick={() => onNavigate('accounts')}
                        className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${
                          currentView === 'accounts'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <Building2 className="h-4 w-4" />
                        <span>Accounts</span>
                      </button>
                      {isNavItemEnabled('opportunities') && (
                      <button
                        onClick={() => onNavigate('opportunities')}
                        className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${
                          currentView === 'opportunities'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <DollarSign className="h-4 w-4" />
                        <span>Opportunities</span>
                      </button>
                      )}
                      <button
                        onClick={() => onNavigate('products')}
                        className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${
                          currentView === 'products'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <Package className="h-4 w-4" />
                        <span>Products</span>
                      </button>
                      {isNavItemEnabled('transactions') && (
                      <button
                        onClick={() => onNavigate('transactions')}
                        className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${
                          currentView === 'transactions'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <Receipt className="h-4 w-4" />
                        <span>Transactions</span>
                      </button>
                      )}
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* BILLING Dropdown - Invoices and Pricebooks */}
                {isNavGroupEnabled('billing') && (
                <div
                  className="relative"
                  onMouseEnter={() => handleDropdownEnter('billing')}
                  onMouseLeave={handleDropdownLeave}
                >
                  <button className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md">
                    <span>Billing</span>
                    <ChevronRight className={`h-3 w-3 transform transition-transform ${openDropdown === 'billing' ? 'rotate-90' : ''}`} />
                  </button>

                  {openDropdown === 'billing' && (
                    <div className="absolute left-0 top-full mt-1 z-50">
                      <div
                        className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl py-2 min-w-[160px]"
                        onMouseEnter={() => handleDropdownEnter('billing')}
                        onMouseLeave={handleDropdownLeave}
                      >
                      <button
                        onClick={() => onNavigate('invoices')}
                        className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${
                          currentView === 'invoices'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <FileText className="h-4 w-4" />
                        <span>Invoices</span>
                      </button>
                      <button
                        onClick={() => onNavigate('pricebooks')}
                        className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${
                          currentView === 'pricebooks'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <BookOpen className="h-4 w-4" />
                        <span>Pricebooks</span>
                      </button>
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* SERVICE Dropdown - Only show if service group is enabled */}
                {isNavGroupEnabled('service') && (
                <div
                  className="relative"
                  onMouseEnter={() => handleDropdownEnter('service')}
                  onMouseLeave={handleDropdownLeave}
                >
                  <button className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md">
                    <span>Service</span>
                    <ChevronRight className={`h-3 w-3 transform transition-transform ${openDropdown === 'service' ? 'rotate-90' : ''}`} />
                  </button>

                  {openDropdown === 'service' && (
                    <div className="absolute left-0 top-full mt-1 z-50">
                      <div
                        className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl py-2 min-w-[160px]"
                        onMouseEnter={() => handleDropdownEnter('service')}
                        onMouseLeave={handleDropdownLeave}
                      >
                      {isNavItemEnabled('cases') && (
                      <button
                        onClick={() => onNavigate('cases')}
                        className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${
                          currentView === 'cases'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <Headphones className="h-4 w-4" />
                        <span>Cases</span>
                      </button>
                      )}
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* PRODUCTIVITY Dropdown - Only show if productivity group is enabled */}
                {isNavGroupEnabled('productivity') && (
                <div
                  className="relative"
                  onMouseEnter={() => handleDropdownEnter('productivity')}
                  onMouseLeave={handleDropdownLeave}
                >
                  <button className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md">
                    <span>Productivity</span>
                    <ChevronRight className={`h-3 w-3 transform transition-transform ${openDropdown === 'productivity' ? 'rotate-90' : ''}`} />
                  </button>

                  {openDropdown === 'productivity' && (
                    <div className="absolute left-0 top-full mt-1 z-50">
                      <div
                        className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl py-2 min-w-[160px]"
                        onMouseEnter={() => handleDropdownEnter('productivity')}
                        onMouseLeave={handleDropdownLeave}
                      >
                      {isNavItemEnabled('tasks') && (
                      <button
                        onClick={() => onNavigate('tasks')}
                        className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${
                          currentView === 'tasks'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <CheckSquare className="h-4 w-4" />
                        <span>Tasks</span>
                      </button>
                      )}
                      {isNavItemEnabled('events') && (
                      <button
                        onClick={() => onNavigate('events')}
                        className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${
                          currentView === 'events'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <Calendar className="h-4 w-4" />
                        <span>Events</span>
                      </button>
                      )}
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* ANALYTICS Dropdown - For managers, supervisors, and system admins; filtered by feature flags */}
                {(userRole === 'manager' || userRole === 'cb_supervisor' || userRole === 'cm_supervisor' || userRole === 'system_admin') && isNavGroupEnabled('analytics') && (
                  <div
                    className="relative"
                    onMouseEnter={() => handleDropdownEnter('analytics')}
                    onMouseLeave={handleDropdownLeave}
                  >
                    <button className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md">
                      <span>Analytics</span>
                      <ChevronRight className={`h-3 w-3 transform transition-transform ${openDropdown === 'analytics' ? 'rotate-90' : ''}`} />
                    </button>

                    {openDropdown === 'analytics' && (
                      <div className="absolute left-0 top-full mt-1 z-50">
                        <div
                          className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl py-2 min-w-[160px]"
                          onMouseEnter={() => handleDropdownEnter('analytics')}
                          onMouseLeave={handleDropdownLeave}
                        >
                        {isNavItemEnabled('dashboard') && (
                        <button
                          onClick={() => onNavigate('dashboard')}
                          className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${
                            currentView === 'dashboard'
                              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                          }`}
                        >
                          <BarChart3 className="h-4 w-4" />
                          <span>Dashboard</span>
                        </button>
                        )}
                        {isNavItemEnabled('reports') && (
                        <button
                          onClick={() => onNavigate('reports')}
                          className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${
                            currentView === 'reports'
                              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                          }`}
                        >
                          <FileText className="h-4 w-4" />
                          <span>Reports</span>
                        </button>
                        )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* System Admin - Moved to Person Icon menu (PE-763) */}
              </nav>
            )}

            {/* Global Search - Available throughout the app */}
            <div className="ml-auto">
              <GlobalSearch onNavigate={onNavigate} userRole={userRole} />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* ThemeToggle hidden until dark mode is fully supported */}
            <div
              className="relative"
              ref={notificationsRef}
            >
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 relative"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-lg shadow-xl z-50 border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Notifications {unreadCount > 0 && `(${unreadCount})`}
                    </h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllAsRead}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>

                  {unreadCount === 0 ? (
                    <div className="px-4 py-8 text-center">
                      <Bell className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No unread notifications
                      </p>
                    </div>
                  ) : (
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.filter(n => !n.isRead).slice(0, 10).map((notification) => (
                        <button
                          key={`${notification.type}-${notification.id}`}
                          onClick={() => handleNotificationClick(notification)}
                          className={`w-full px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 text-left border-b border-gray-100 dark:border-gray-800 last:border-b-0 transition-colors ${
                            notification.isRead ? 'opacity-60' : 'bg-blue-50/50 dark:bg-blue-900/10'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {/* Unread indicator */}
                            <div className="flex-shrink-0 mt-2">
                              {!notification.isRead ? (
                                <div className="w-2 h-2 bg-blue-500 rounded-full" />
                              ) : (
                                <div className="w-2 h-2" />
                              )}
                            </div>

                            {/* Icon */}
                            <div className={`mt-0.5 p-1.5 rounded-full ${
                              notification.priority === 'Critical' || notification.priority === 'Urgent'
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                                : notification.priority === 'High'
                                ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
                                : notification.type === 'support_ticket'
                                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                                : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                            }`}>
                              {notification.type === 'task' ? (
                                <CheckSquare className="h-3.5 w-3.5" />
                              ) : notification.type === 'support_ticket' ? (
                                <Ticket className="h-3.5 w-3.5" />
                              ) : (
                                <Briefcase className="h-3.5 w-3.5" />
                              )}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium">
                                  {notification.type === 'support_ticket' ? 'Support Ticket' : notification.type}
                                </span>
                                <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                  notification.priority === 'Critical'
                                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                                    : notification.priority === 'Urgent'
                                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                    : notification.priority === 'High'
                                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                                }`}>
                                  {notification.priority}
                                </span>
                              </div>
                              <p className={`text-sm truncate mt-1 ${
                                notification.isRead
                                  ? 'font-normal text-gray-700 dark:text-gray-300'
                                  : 'font-medium text-gray-900 dark:text-gray-100'
                              }`}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {notification.message}
                              </p>
                              {notification.timestamp && (
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                  {formatNotificationTime(notification.timestamp)}
                                </p>
                              )}
                            </div>

                            {/* Arrow */}
                            <ChevronRight className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Always show View All link to access read notifications */}
                  <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                    <button
                      onClick={() => {
                        onNavigate('tasks');
                        setShowNotifications(false);
                      }}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    >
                      View all notifications
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div
              className="relative"
              ref={userMenuRef}
            >
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <User className="h-5 w-5" />
              </button>
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-600">
                  <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-600">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {user?.email || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Role: {userRole}</p>
                  </div>
                  <button
                    onClick={() => {
                      onNavigate('profile');
                      setShowUserMenu(false);
                    }}
                    className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <User className="h-4 w-4 mr-2" />
                    Your Profile
                  </button>
                  <button
                    onClick={() => {
                      onNavigate('settings');
                      setShowUserMenu(false);
                    }}
                    className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </button>
                  <button
                    onClick={() => {
                      onNavigate('support-tickets');
                      setShowUserMenu(false);
                    }}
                    className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Bell className="h-4 w-4 mr-2" />
                    Support Tickets
                  </button>
                  <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                  {/* System Admin - Available to all users in MVP mode */}
                  <button
                    onClick={() => {
                      onNavigate('system-admin');
                      setShowUserMenu(false);
                    }}
                    className={`flex items-center w-full text-left px-4 py-2 text-sm ${
                      currentView === 'system-admin'
                        ? 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    System Admin
                  </button>
                  <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                  <button
                    onClick={async () => {
                      setShowUserMenu(false);
                      await logout();
                    }}
                    className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default GlobalNavigation;